import * as echarts from 'echarts'
import lodash from 'lodash'
import React from 'react'
import { chartValueLabelStyle, classToFComponent } from 'src/utils/views'
import { makeStyles } from 'tss-react/mui'
import { Logger } from 'wdc-cube'
import { EChartViewClass } from './base-echart'

import { ChartFaixasScope } from '../../tcm_scopes'
import { ChartFontSize, ChartPalette, axisLabelStyle, seriesColor, tooltipStyle } from './tcm_chart_theme'
import { formatNumberToCompactShortForm } from './tcm_view_utils'
import { TcmCard } from 'src/utils/views/modules/the-consumer-market'

const LOG = Logger.get('TCM-FAIXAS')

const useStyles = makeStyles()({
    view: {
        display: 'flex',
        alignSelf: 'stretch',
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: 0,
        minHeight: 0,
        overflowX: 'hidden',
        overflowY: 'auto'
    }
})

type ChartFaixasProps = {
    className?: string
    style?: React.CSSProperties
    scope: ChartFaixasScope
}

class ChartFaixasViewClass extends EChartViewClass<ChartFaixasProps> {
    // :: Fields
    scope!: ChartFaixasScope
    classes!: ReturnType<typeof useStyles>['classes']

    onSyncState(props: ChartFaixasProps) {
        this.scope = props.scope
        this.classes = useStyles().classes
        this.chartData = buildChartData(this.scope)
        this.loaded = this.scope.loaded
        super.onSyncState(props)
    }

    override render(props: ChartFaixasProps): React.JSX.Element {
        const { scope } = this
        return (
            <TcmCard caption={scope.caption} className={props.className} style={props.style}>
                {super.render({ className: this.classes.view, scope: props.scope })}
            </TcmCard>
        )
    }

    protected override onComputeSize(opts: echarts.ResizeOpts) {
        if (this.scope.dataset.length > 0) {
            opts.height = Math.max(this.scope.dataset.length * 30, opts.height as number)
        }
    }

    protected override onBindChartEvents(chart: echarts.ECharts) {
        chart.off('click', this.emitClickOnSeries)
        chart.on('click', this.emitClickOnSeries)
    }

    emitClickOnSeries = (evt: echarts.ECElementEvent) => {
        if (!lodash.isNumber(evt.dataIndex)) {
            return
        }

        this.scope.onItemClicked(evt.dataIndex).catch(LOG.caught)
    }
}

export default classToFComponent(ChartFaixasViewClass, React)

function buildChartData(scope: ChartFaixasScope): echarts.EChartsOption {
    const yAxisData: {
        value: string
        textStyle: {
            color: string
            fontSize: number
        }
    }[] = []

    const xAxisData: {
        id: number
        value: number
        itemStyle: {
            color: string
        }
    }[] = []

    for (let rowIdx = 0; rowIdx < scope.dataset.length; rowIdx++) {
        const row = scope.dataset[rowIdx]
        const quantidade = row[0]
        const descricao = row[1]
        const selected = row[2]

        yAxisData.push({
            value: descricao.length > 35 ? `${descricao.substring(0, 32)}...` : descricao,
            textStyle: {
                color: selected ? ChartPalette.selected : ChartPalette.label,
                fontSize: ChartFontSize.axis
            }
        })

        xAxisData.push({
            id: rowIdx,
            value: quantidade,
            itemStyle: {
                color: seriesColor(selected)
            }
        })
    }

    return {
        title: {
            show: false
        },
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'shadow'
            },
            ...tooltipStyle
        },
        grid: { containLabel: true, top: 0, left: 10, bottom: 5 },
        xAxis: {
            name: '',
            axisLabel: {
                show: false
            },
            triggerEvent: true,
            splitLine: { show: false }
        },
        yAxis: {
            type: 'category',
            axisLabel: {
                ...axisLabelStyle,
                width: 200
            },
            axisTick: { show: false },
            axisLine: { show: false },
            triggerEvent: true,
            splitLine: { show: false },
            data: yAxisData
        },
        series: [
            {
                type: 'bar',
                itemStyle: { borderRadius: [0, 3, 3, 0] },
                label: {
                    show: true,
                    align: 'left',
                    position: 'right',
                    color: chartValueLabelStyle.color,
                    fontSize: ChartFontSize.value,
                    fontWeight: 700,
                    formatter: (params) => {
                        const amountVal =
                            typeof params.value === 'number'
                                ? (params.value as number)
                                : (params.value as [number, string])[0]
                        return formatNumberToCompactShortForm(amountVal)
                    }
                },
                data: xAxisData
            }
        ]
    }
}
